/**
 * A nota que foi emitida mas cujo painel a tela pode ter perdido. [DA-599]
 *
 * O buraco medido em 21/08/2026 (VND-001965, R$ 150,00): a emissao levou 11 segundos — contra os
 * 0,4s que sao a rotina da casa — e o painel de impressao nunca chegou na frente da vendedora. A
 * nota estava AUTORIZADA na SEFAZ (NFC-e n1753) e o `200` voltou pro navegador dela; o que se
 * perdeu foi so a tela.
 *
 * ⚠️ **O `setResultadoEmissao` mora dentro do CaixaPainel, e por isso o resultado tem a vida util
 * de uma montagem.** Se a tela remontar, recarregar, ou se alguem fechar o painel por engano no
 * segundo em que ele aparece, a nota some do fluxo do caixa — sem erro, sem log e sem segunda
 * chance (so pela tela de Notas Fiscais, que a vendedora nao sabe que precisa procurar).
 *
 * Esta peca guarda a INTENCAO ("estou emitindo a nota do orcamento X") fora do React, pra que a
 * proxima montagem do caixa possa perguntar ao servidor se a nota saiu e reabrir o painel.
 *
 * ⚠️ A marca e' apagada por FECHAMENTO DELIBERADO (o botao Concluir), nunca pelo simples abrir do
 * painel: quem fechou de proposito ja viu a nota e nao quer ela de volta a cada F5.
 *
 * ⚠️ `sessionStorage` (nao `localStorage`): a marca tem que morrer quando a aba fecha. Uma nota de
 * ontem reaparecendo na abertura do caixa seria pior que nao reaparecer nenhuma.
 */

/** O que a tela precisa saber pra reabrir o painel sem o contexto da venda em memoria. */
export interface NotaPendente {
  orcamentoId: number;
  tipo: 'nfce' | 'nfe';
  clienteWhatsapp?: string | null;
  clienteNome?: string | null;
  clienteId?: number | null;
  valorTotal?: string | number | null;
}

const CHAVE = 'fiodecobre.nota_pendente';

/**
 * O acesso vai sempre embrulhado: em aba anonima, com cookies de site bloqueados, ou dentro de um
 * iframe restrito, o proprio `sessionStorage` ESTOURA no acesso — e um resgate que nunca deveria
 * atrapalhar ninguem nao pode derrubar a tela de quem esta com o cliente no balcao.
 */
function comStore<T>(fn: (s: Storage) => T, store: Storage | undefined, padrao: T): T {
  try {
    const s = store ?? window.sessionStorage;
    if (!s) return padrao;
    return fn(s);
  } catch {
    return padrao;
  }
}

/** Marca que uma emissao comecou. Chamar ANTES do POST — o que se perde e' justamente a volta. */
export function marcarNotaPendente(nota: NotaPendente, store?: Storage): void {
  comStore((s) => s.setItem(CHAVE, JSON.stringify(nota)), store, undefined);
}

/**
 * Le a marca. Devolve `null` quando nao ha nada, quando o conteudo nao e' JSON valido (aba antiga,
 * chave sobrescrita por outra versao do app) ou quando falta o essencial pra reabrir o painel —
 * marca pela metade vira `null`, nunca uma consulta com `orcamentoId` indefinido.
 */
export function lerNotaPendente(store?: Storage): NotaPendente | null {
  return comStore<NotaPendente | null>((s) => {
    const cru = s.getItem(CHAVE);
    if (!cru) return null;
    const n = JSON.parse(cru) as NotaPendente;
    if (!n || typeof n.orcamentoId !== 'number' || !Number.isFinite(n.orcamentoId)) return null;
    if (n.tipo !== 'nfce' && n.tipo !== 'nfe') return null;
    return n;
  }, store, null);
}

/** Apaga a marca. Chamar no fechamento DELIBERADO do painel (e ao desistir da emissao). */
export function limparNotaPendente(store?: Storage): void {
  comStore((s) => s.removeItem(CHAVE), store, undefined);
}
